/**
 * ViewModal Component
 *
 * Displays detailed information about an item with actions
 * for editing, deleting, and marking as processed.
 */

import {
  ExternalLink,
  Edit2,
  Trash2,
  Star,
  Calendar,
  Clock,
  File,
  Tag,
  CheckCircle,
  Inbox,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { formatFullDate, formatDateTime } from '@/utils/dates';
import { getYouTubeVideoId } from '@/services/metadata';
import { Button } from '@/components/ui/button';
import { TagBadge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui/dialog';
import { RichTextViewer } from '@/components/RichTextEditor';
import { TwitterEmbed } from '@/components/embeds/TwitterEmbed';
import { TaskCheckbox } from '@/components/ui/checkbox';
import { ItemChat } from '@/components/ai/ItemChat';
import { useData } from '@/context/DataContext';
import type { Item, LinkItem, PodcastItem, NoteItem, IdeaItem, FileItem, TaskItem } from '@/types';
import { isLinkItem, isPodcastItem, isNoteItem, isIdeaItem, isFileItem, isTaskItem } from '@/types';

// ============================================
// TYPES
// ============================================

export interface ViewModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** The item to display */
  item: Item | null;
  /** Close handler */
  onClose: () => void;
  /** Edit handler */
  onEdit: (item: Item) => void;
  /** Delete handler */
  onDelete: (item: Item) => void;
  /** Mark as processed handler (removes 'inbox' tag) */
  onMarkProcessed?: ((item: Item) => void) | undefined;
  /** Move to inbox handler (adds 'inbox' tag) */
  onMoveToInbox?: ((item: Item) => void) | undefined;
  /** Toggle favorite handler */
  onToggleFavorite?: ((item: Item) => void) | undefined;
  /** Toggle task completion handler */
  onToggleComplete?: ((item: TaskItem, completed: boolean) => void) | undefined;
  /** Tag click handler */
  onTagClick?: ((tag: string) => void) | undefined;
}

// ============================================
// HELPERS
// ============================================

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ============================================
// SECTION COMPONENTS
// ============================================

interface MetadataSectionProps {
  label: string;
  value: string | null | undefined;
  icon?: typeof Calendar | undefined;
  link?: string | undefined;
}

function MetadataSection({ label, value, icon: Icon, link }: MetadataSectionProps) {
  if (!value) return null;

  const content = (
    <div className="flex items-start gap-2 text-sm">
      {Icon && <Icon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />}
      <div>
        <span className="text-gray-500">{label}:</span>{' '}
        <span className={cn('text-gray-900', link && 'text-brand-600 hover:underline')}>
          {value}
        </span>
      </div>
    </div>
  );

  if (link) {
    return (
      <a href={link} target="_blank" rel="noopener noreferrer" className="block">
        {content}
      </a>
    );
  }

  return content;
}

// ============================================
// TYPE-SPECIFIC VIEWS
// ============================================

interface LinkViewProps {
  item: LinkItem;
}

/**
 * Check if URL is a Twitter/X link
 */
function isTwitterUrl(url: string): boolean {
  return url.includes('twitter.com/') || url.includes('x.com/');
}

function LinkView({ item }: LinkViewProps) {
  const youtubeId = getYouTubeVideoId(item.url);
  const isTwitter = isTwitterUrl(item.url);

  return (
    <div className="space-y-4">
      {/* YouTube embed */}
      {youtubeId && (
        <div className="aspect-video w-full overflow-hidden rounded-lg bg-gray-900">
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}`}
            className="w-full h-full"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            title="YouTube video"
          />
        </div>
      )}

      {/* Twitter embed */}
      {isTwitter && !youtubeId && <TwitterEmbed url={item.url} />}

      {/* Regular thumbnail (not for embeddable content) */}
      {item.thumbnail && !youtubeId && !isTwitter && (
        <div className="aspect-video w-full overflow-hidden rounded-lg bg-gray-100">
          <img src={item.thumbnail} alt="" className="h-full w-full object-cover" />
        </div>
      )}

      {/* URL (always show for non-embedded content) */}
      {!youtubeId && !isTwitter && (
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-brand-600 hover:underline text-sm"
        >
          <ExternalLink className="h-4 w-4" />
          {item.url}
        </a>
      )}

      {/* Site info */}
      {item.siteName && <MetadataSection label="Site" value={item.siteName} />}

      {/* Description */}
      {item.description && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-1">Description</h4>
          <p className="text-sm text-gray-600">{item.description}</p>
        </div>
      )}

      {/* Personal notes */}
      {item.personalNotes && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-1">Personal Notes</h4>
          <p className="text-sm text-gray-600 whitespace-pre-wrap">{item.personalNotes}</p>
        </div>
      )}
    </div>
  );
}

interface PodcastViewProps {
  item: PodcastItem;
}

/**
 * Check if URL is a direct audio file
 */
function isDirectAudioUrl(url: string): boolean {
  return /\.(mp3|m4a|wav|ogg|aac)(\?|$)/i.test(url);
}

function PodcastView({ item }: PodcastViewProps) {
  const youtubeId = getYouTubeVideoId(item.url);
  const isDirectAudio = isDirectAudioUrl(item.url);

  return (
    <div className="space-y-4">
      {/* YouTube embed for video podcasts */}
      {youtubeId && (
        <div className="aspect-video w-full overflow-hidden rounded-lg bg-gray-900">
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}`}
            className="w-full h-full"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            title="YouTube video"
          />
        </div>
      )}

      {/* HTML5 audio player for direct audio files */}
      {isDirectAudio && (
        <div className="p-4 bg-gray-50 rounded-lg">
          <audio controls className="w-full" preload="metadata">
            <source src={item.url} />
            Your browser does not support the audio element.
          </audio>
        </div>
      )}

      {/* Thumbnail (for non-embeddable content) */}
      {item.thumbnail && !youtubeId && (
        <div className="aspect-square w-32 overflow-hidden rounded-lg bg-gray-100 mx-auto">
          <img src={item.thumbnail} alt="" className="h-full w-full object-cover" />
        </div>
      )}

      {/* External link (for non-embeddable content) */}
      {!youtubeId && !isDirectAudio && (
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-brand-600 hover:underline text-sm"
        >
          <ExternalLink className="h-4 w-4" />
          Listen to Episode
        </a>
      )}

      {/* Podcast info */}
      {item.podcastName && <MetadataSection label="Podcast" value={item.podcastName} />}

      {/* Description */}
      {item.description && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-1">Description</h4>
          <p className="text-sm text-gray-600">{item.description}</p>
        </div>
      )}

      {/* Personal notes */}
      {item.personalNotes && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-1">Personal Notes</h4>
          <p className="text-sm text-gray-600 whitespace-pre-wrap">{item.personalNotes}</p>
        </div>
      )}
    </div>
  );
}

interface NoteViewProps {
  item: NoteItem;
}

function NoteView({ item }: NoteViewProps) {
  return (
    <div className="space-y-4">
      {/* Description */}
      {item.description && <p className="text-sm text-gray-600">{item.description}</p>}

      {/* Content */}
      <div className="border-t pt-4">
        <RichTextViewer content={item.content} />
      </div>
    </div>
  );
}

interface IdeaViewProps {
  item: IdeaItem;
}

function IdeaView({ item }: IdeaViewProps) {
  return (
    <div className="space-y-4">
      {/* Description */}
      {item.description && <p className="text-sm text-gray-600">{item.description}</p>}

      {/* Content */}
      <div className="border-t pt-4">
        <RichTextViewer content={item.content} />
      </div>
    </div>
  );
}

interface FileViewProps {
  item: FileItem;
}

function FileView({ item }: FileViewProps) {
  return (
    <div className="space-y-4">
      {/* File info card */}
      <div className="flex items-center gap-4 p-4 rounded-lg bg-gray-50 border">
        <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-white border">
          <File className="h-6 w-6 text-gray-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 truncate">{item.originalFileName}</p>
          <p className="text-sm text-gray-500">
            {formatFileSize(item.fileSize)} &middot; {item.mimeType}
          </p>
        </div>
        <a
          href={item.webViewLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-shrink-0"
        >
          <Button variant="outline" size="sm">
            <ExternalLink className="h-4 w-4 mr-2" />
            Open
          </Button>
        </a>
      </div>

      {/* Description */}
      {item.description && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-1">Description</h4>
          <p className="text-sm text-gray-600">{item.description}</p>
        </div>
      )}

      {/* Personal notes */}
      {item.personalNotes && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-1">Personal Notes</h4>
          <p className="text-sm text-gray-600 whitespace-pre-wrap">{item.personalNotes}</p>
        </div>
      )}
    </div>
  );
}

interface TaskViewProps {
  item: TaskItem;
  onToggleComplete?: ((item: TaskItem, completed: boolean) => void) | undefined;
}

function TaskView({ item, onToggleComplete }: TaskViewProps) {
  return (
    <div className="flex items-center gap-3">
      <TaskCheckbox
        checked={item.isCompleted}
        onChange={(checked) => onToggleComplete?.(item, checked)}
      />
      <span className={cn('text-lg font-medium', item.isCompleted && 'line-through text-gray-500')}>
        {item.title}
      </span>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function ViewModal({
  isOpen,
  item,
  onClose,
  onEdit,
  onDelete,
  onMarkProcessed,
  onMoveToInbox,
  onToggleFavorite,
  onToggleComplete,
  onTagClick,
}: ViewModalProps) {
  const { updateItem } = useData();

  if (!item) return null;

  const hasInboxTag = item.tags.includes('inbox');
  const { date } = formatDateTime(item.createdAt);

  // Handle mark processed
  const handleMarkProcessed = () => {
    if (onMarkProcessed && hasInboxTag) {
      onMarkProcessed(item);
    }
  };

  // Render type-specific content
  const renderContent = () => {
    if (isLinkItem(item)) return <LinkView item={item} />;
    if (isPodcastItem(item)) return <PodcastView item={item} />;
    if (isNoteItem(item)) return <NoteView item={item} />;
    if (isIdeaItem(item)) return <IdeaView item={item} />;
    if (isFileItem(item)) return <FileView item={item} />;
    if (isTaskItem(item)) return <TaskView item={item} onToggleComplete={onToggleComplete} />;
    return null;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <DialogTitle className="flex items-center gap-2">
                {item.title}
                {item.isFavorite && <Star className="h-5 w-5 fill-amber-400 text-amber-400" />}
              </DialogTitle>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="flex-1 overflow-y-auto">
          {/* Type-specific content */}
          {renderContent()}

          {/* AI Chat */}
          <ItemChat item={item} onUpdateItem={updateItem} />

          {/* Tags */}
          {item.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t">
              <Tag className="h-4 w-4 text-gray-400" />
              {item.tags.map((tag) => (
                <TagBadge key={tag} tag={tag} onClick={onTagClick} />
              ))}
            </div>
          )}

          {/* Metadata */}
          <div className="mt-6 pt-4 border-t space-y-2 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Created on {date}
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Last updated {formatFullDate(item.updatedAt)}
            </div>
          </div>
        </DialogBody>

        <DialogFooter className="flex-wrap gap-2">
          {/* Mark Processed button (for items in inbox) */}
          {hasInboxTag && onMarkProcessed && (
            <Button variant="outline" onClick={handleMarkProcessed} className="mr-auto">
              <CheckCircle className="h-4 w-4 mr-2" />
              Mark Processed
            </Button>
          )}

          {/* Move to Inbox button (for items NOT in inbox) */}
          {!hasInboxTag && onMoveToInbox && (
            <Button variant="outline" onClick={() => onMoveToInbox(item)} className="mr-auto">
              <Inbox className="h-4 w-4 mr-2" />
              Move to Inbox
            </Button>
          )}

          {/* Toggle Favorite */}
          {onToggleFavorite && (
            <Button variant="ghost" onClick={() => onToggleFavorite(item)}>
              <Star
                className={cn('h-4 w-4 mr-2', item.isFavorite && 'fill-amber-400 text-amber-400')}
              />
              {item.isFavorite ? 'Unfavorite' : 'Favorite'}
            </Button>
          )}

          {/* Delete button */}
          <Button
            variant="ghost"
            onClick={() => onDelete(item)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>

          {/* Edit button */}
          <Button onClick={() => onEdit(item)}>
            <Edit2 className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
